import { useEffect, useState } from "react";
import { GridMetaData, createUrlSearchParams, filtersEqual, sortsEqual } from "models/grids";
import { ProjectStatus } from "models/projects";
import { useCurrentUser, useCentralApi } from "hooks/shared";
import { IMyWorkItem } from "models/work-items/IMyWorkItem";
import IWorkItemWorkflow from "models/work-items/IWorkItemWorkflow";
import IWorkItemResourceAllocation from "models/work-items/IWorkItemResourceAllocation";

type GetWorkItemResourceAllocationResult = {
    resourceAllocation: IWorkItemResourceAllocation[],
    total: number
};

type WorkItemResourceAllocationResult = [
    IWorkItemResourceAllocation[],
    number,
    boolean,
    string | null,
    () => void
];

type UseWorkItemWorkflowsSignature = 
(
    workitemId: number,
    revalidate?: () => void,
    draft?: boolean,
)  => WorkItemResourceAllocationResult;

const useWorkItemResourceAllocation: UseWorkItemWorkflowsSignature = (workitemId, revalidate,  draft = false) => {
    const [result, setResult] = useState<IWorkItemResourceAllocation[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const user = useCurrentUser();
    const fetcher = useCentralApi(user);

    const fetchData = async () => {
        if (workitemId === 0) {
            return;
        }
        if (user && user.id_token) {
            setLoading(true);
            try {
                const resp = await fetcher(draft ? `/api/v3/drafts/workitems/${workitemId}/resource-allocation` :
                                                    `/api/v3/workitems/${workitemId}/resource-allocation`);
                if (!resp.ok) {
                    // RESPOND TO ERRORS
                }

                if(draft && revalidate){
                    revalidate();
                }

                const data = await resp.json() as GetWorkItemResourceAllocationResult;
                setResult(data.resourceAllocation);
                setTotal(data.total);
            } catch (err) {
                console.log(err);
            } finally {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        fetchData();
    }, [user, workitemId]);

    return [
        result,
        total,
        loading,
        error,
        fetchData,  // Return the function to allow refetching data
    ];
};

export default useWorkItemResourceAllocation;
